/**
 * API Client Utility
 * Provides a simplified fetch wrapper for API requests.
 * Access token is automatically included via HttpOnly cookie.
 */

const ApiClient = {
    // Request timeout in milliseconds (30 seconds)
    REQUEST_TIMEOUT: 30000,
    
    // Maximum retry attempts for transient failures
    MAX_RETRIES: 2,
    
    // Retry delay in milliseconds
    RETRY_DELAY: 1000,
    
    // Track if we're already handling a 401 to prevent multiple redirects
    _handling401: false,

    /**
     * Creates headers with default Content-Type
     * @param {HeadersInit} [additionalHeaders] Additional headers to include
     * @returns {Headers} Headers object
     */
    createHeaders(additionalHeaders = {}) {
        const headers = new Headers(additionalHeaders);
        
        // Set Content-Type to application/json if not already set
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }

        return headers;
    },

    /**
     * Creates an AbortController with timeout
     * @param {number} ms Milliseconds to wait before aborting
     * @returns {AbortController} AbortController that will abort after timeout
     */
    createAbortControllerWithTimeout(ms) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
            controller.abort();
        }, ms);
        
        // Store timeout ID so it can be cleared if needed
        controller._timeoutId = timeoutId;
        
        return controller;
    },

    /**
     * Handles 401 Unauthorized responses
     * Prevents multiple simultaneous redirects
     */
    handle401Unauthorized() {
        // Prevent multiple simultaneous 401 handlers
        if (this._handling401) {
            return;
        }
        
        this._handling401 = true;
        
        // Clear any stale session data
        if (window.vueAppInstance && typeof window.vueAppInstance.handleSessionExpired === 'function') {
            window.vueAppInstance.handleSessionExpired();
        } else {
            // Fallback: redirect to home page which will show login
            window.location.href = '/?sessionExpired=true';
        }
    },

    /**
     * Determines if an error is retryable (transient network error)
     * @param {Error} error The error to check
     * @returns {boolean} True if the error is retryable
     */
    isRetryableError(error) {
        // Retry on network errors, timeouts, or 5xx server errors
        if (!error) return false;
        
        const errorMessage = error.message || error.toString();
        const isNetworkError = errorMessage.includes('Failed to fetch') || 
                              errorMessage.includes('NetworkError') ||
                              errorMessage.includes('timeout') ||
                              errorMessage.includes('Network request failed');
        
        return isNetworkError;
    },

    /**
     * Fetch wrapper for API requests
     * Access token is automatically included via HttpOnly cookie
     * Automatically handles 401 Unauthorized responses
     * Includes timeout and retry logic for transient failures
     * @param {string|Request} url The URL or Request object
     * @param {RequestInit} [options] Fetch options
     * @param {number} [retryCount] Current retry attempt (internal use)
     * @returns {Promise<Response>} The fetch response
     */
    async fetch(url, options = {}, retryCount = 0) {
        // Check if body is FormData or URLSearchParams
        const isFormData = options.body instanceof FormData;
        const isURLSearchParams = options.body instanceof URLSearchParams;
        
        // Merge headers
        const headers = this.createHeaders(options.headers);

        // For FormData or URLSearchParams, don't set Content-Type (let browser set it with boundary)
        if (isFormData || isURLSearchParams) {
            headers.delete('Content-Type');
        }

        // Create new options with merged headers
        const fetchOptions = {
            ...options,
            headers: headers,
            credentials: 'include' // Ensure cookies are sent with requests
        };

        // If body is an object and Content-Type is JSON, stringify it
        if (fetchOptions.body && typeof fetchOptions.body === 'object' && 
            !isFormData && 
            !isURLSearchParams &&
            headers.get('Content-Type')?.includes('application/json')) {
            fetchOptions.body = JSON.stringify(fetchOptions.body);
        }

        try {
            // Create abort controller with timeout (only if no signal is already provided)
            let abortController = null;
            if (!fetchOptions.signal) {
                abortController = this.createAbortControllerWithTimeout(this.REQUEST_TIMEOUT);
                fetchOptions.signal = abortController.signal;
            }
            
            const response = await window.fetch(url, fetchOptions);
            
            // Clear timeout since request completed (if we created one)
            if (abortController && abortController._timeoutId) {
                clearTimeout(abortController._timeoutId);
            }
            
            // Handle 401 Unauthorized (session expired) automatically
            if (response.status === 401) {
                this.handle401Unauthorized();
                // Return the response but don't process it further
                return response;
            }
            
            // Handle 5xx server errors with retry
            if (response.status >= 500 && response.status < 600 && retryCount < this.MAX_RETRIES) {
                await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * (retryCount + 1)));
                return this.fetch(url, options, retryCount + 1);
            }
            
            return response;
        } catch (error) {
            // Handle AbortError (timeout) specifically
            if (error.name === 'AbortError') {
                const timeoutError = new Error(`Request timeout after ${this.REQUEST_TIMEOUT}ms`);
                timeoutError.name = 'TimeoutError';
                
                // Retry timeout errors if we haven't exceeded max retries
                if (retryCount < this.MAX_RETRIES) {
                    await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * (retryCount + 1)));
                    return this.fetch(url, options, retryCount + 1);
                }
                
                throw timeoutError;
            }
            
            // Handle network errors, timeouts, and other fetch failures
            const isRetryable = this.isRetryableError(error) && retryCount < this.MAX_RETRIES;
            
            if (isRetryable) {
                // Wait before retrying with exponential backoff
                await new Promise(resolve => setTimeout(resolve, this.RETRY_DELAY * (retryCount + 1)));
                return this.fetch(url, options, retryCount + 1);
            }
            
            // Re-throw non-retryable errors or if max retries exceeded
            throw error;
        }
    },

    /**
     * Convenience method for GET requests
     * @param {string} url The URL to fetch
     * @param {RequestInit} [options] Additional fetch options
     * @returns {Promise<Response>} The fetch response
     */
    async get(url, options = {}) {
        return this.fetch(url, {
            ...options,
            method: 'GET'
        });
    },

    /**
     * Convenience method for POST requests
     * @param {string} url The URL to fetch
     * @param {any} [body] The request body (will be JSON stringified)
     * @param {RequestInit} [options] Additional fetch options
     * @returns {Promise<Response>} The fetch response
     */
    async post(url, body = null, options = {}) {
        return this.fetch(url, {
            ...options,
            method: 'POST',
            body: body
        });
    },

    /**
     * Convenience method for PUT requests
     * @param {string} url The URL to fetch
     * @param {any} [body] The request body (will be JSON stringified)
     * @param {RequestInit} [options] Additional fetch options
     * @returns {Promise<Response>} The fetch response
     */
    async put(url, body = null, options = {}) {
        return this.fetch(url, {
            ...options,
            method: 'PUT',
            body: body
        });
    },

    /**
     * Convenience method for DELETE requests
     * @param {string} url The URL to fetch
     * @param {RequestInit} [options] Additional fetch options
     * @returns {Promise<Response>} The fetch response
     */
    async delete(url, options = {}) {
        return this.fetch(url, {
            ...options,
            method: 'DELETE'
        });
    },

    /**
     * Convenience method for PATCH requests
     * @param {string} url The URL to fetch
     * @param {any} [body] The request body (will be JSON stringified)
     * @param {RequestInit} [options] Additional fetch options
     * @returns {Promise<Response>} The fetch response
     */
    async patch(url, body = null, options = {}) {
        return this.fetch(url, {
            ...options,
            method: 'PATCH',
            body: body
        });
    },

    /**
     * Handles JSON response and throws on error status
     * Automatically handles 401 Unauthorized by redirecting to login
     * @param {Response} response The fetch response
     * @returns {Promise<any>} The parsed JSON data
     * @throws {Error} If response is not ok
     */
    async handleJsonResponse(response) {
        if (!response.ok) {
            // Handle 401 Unauthorized (session expired)
            if (response.status === 401) {
                this.handle401Unauthorized();
                // Try to get error message, but don't fail if body is already consumed
                try {
                    const errorData = await response.json().catch(() => ({ error: 'Unauthorized', message: 'Session expired' }));
                    throw new Error(errorData.message || 'Session expired. Please log in again.');
                } catch (error) {
                    // If it's already our error, re-throw it
                    if (error instanceof Error && error.message.includes('Session expired')) {
                        throw error;
                    }
                    // Otherwise, throw a generic session expired error
                    throw new Error('Session expired. Please log in again.');
                }
            }
            
            // For other errors, try to get error text
            let errorText = '';
            try {
                // Clone response to read body without consuming it
                const clonedResponse = response.clone();
                errorText = await clonedResponse.text();
            } catch (err) {
                errorText = response.statusText || 'Unknown error';
            }
            
            throw new Error(`API Error: ${response.status} ${response.statusText}${errorText ? ' - ' + errorText : ''}`);
        }
        
        // Parse JSON response
        try {
            return await response.json();
        } catch (error) {
            // If JSON parsing fails, throw a more descriptive error
            throw new Error(`Failed to parse JSON response: ${error.message}`);
        }
    },

    /**
     * Handles text response and throws on error status
     * @param {Response} response The fetch response
     * @returns {Promise<string>} The response text
     * @throws {Error} If response is not ok
     */
    async handleTextResponse(response) {
        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
        }
        return await response.text();
    }
};

// Export for use in modules, or attach to window for global access
if (typeof module !== 'undefined' && module.exports) {
    module.exports = ApiClient;
} else {
    window.ApiClient = ApiClient;
}

